import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { LoggingService } from '../services/logging.service';
import { ContextService } from '../logging/services/context.service';
import { LogQueueManager } from '../logging/services/log-queue-manager.service';
import { WriteLoggingService } from '../logging/services/write-logging.service';
import { GlobalErrorHandler, LOGGING_SERVICE } from '../logging/services/global-error-handler.service';
import { KinectiveHeadersInterceptor } from '../logging/interceptors/kinective-headers.interceptor';
import { environment } from 'src/environments/environment';

/**
 * Initialize logging system before application startup
 * 
 * This factory function:
 * 1. Bridges console methods to intercept all console output
 * 2. Initializes context with application startup metadata
 * 
 * Note: This is kept minimal to avoid blocking application startup.
 * CloudWatch logging and other features are enabled after app initialization.
 * 
 * @param loggingService The LoggingService instance
 * @param contextService The ContextService instance
 * @returns Factory function for APP_INITIALIZER
 */
export function initializeLogging(
  loggingService: LoggingService,
  contextService: ContextService
): () => void {
  return () => {
    try {
      // Bridge console methods before application starts
      loggingService.bridgeConsole();
      
      // Initialize context service with application startup
      contextService.updateComponentContext('app', 'startup');
      
      // Log application initialization (development only)
      if (!environment.production) {
        console.log('Ground Control logging system initialized');
      }
    } catch (error) {
      // Ensure initialization doesn't block application startup
      console.error('Error initializing logging system:', error);
    }
  };
}

/**
 * LoggingModule provides logging functionality
 * with console bridging, CloudWatch integration, and error handling.
 * 
 * This module should be imported in the root AppModule.
 * 
 * Features:
 * - Console method interception and bridging
 * - Structured logging with context enrichment
 * - CloudWatch log transmission with batching and retry
 * - Automatic error capture via GlobalErrorHandler
 * - Graceful degradation and fallback mechanisms
 */
@NgModule({
  providers: [
    // Core logging services
    LoggingService,
    ContextService,
    LogQueueManager,
    WriteLoggingService,
    
    // Replace default Angular ErrorHandler with GlobalErrorHandler
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },

    // Expose LoggingService under the LOGGING_SERVICE token for optional injection
    {
      provide: LOGGING_SERVICE,
      useExisting: LoggingService
    },
    
    // HTTP Interceptor for adding Kinective headers
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KinectiveHeadersInterceptor,
      multi: true
    },
    
    // Initialize logging before application startup
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLogging,
      deps: [LoggingService, ContextService],
      multi: true
    }
  ]
})
export class LoggingModule {
  constructor(
    private loggingService: LoggingService,
    private logQueueManager: LogQueueManager
  ) {
    // CloudWatch logging will be enabled after user authentication
    // This prevents blocking during app startup and ensures we have user context
    if (!environment.production) {
      console.log('CloudWatch logging will be enabled after user login');
    }

    // Flush logs on page unload to ensure no logs are lost
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        // Use sendBeacon for reliable log transmission during page unload
        this.loggingService.flushWithBeacon();
      });
      
      // Also handle page visibility changes for mobile/tab switching
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.loggingService.flushWithBeacon();
        }
      });
    }
  }
}
